import { Injectable }                        from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';

import { Observable }      from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment }                                 from '@environment/environment';
import { TransformClass }                              from '@utils/transform';
import { ProofreaderRpaUpload }                        from '@models/proofreader/proofreader-rpa-upload';
import { ProofreaderReport }                           from '@models/proofreader/proofreader-report';
import { MonitoringReportItem }                        from '@models/proofreader/monitoring-report-item';
import { RestResponse }                                from '@models/rest';
import { EditMonitoringParams }                        from '@models/proofreader/edit-monitoring-params';
import { SearchReportProofreaders }                    from '@models/proofreader/search-report-proofreaders';
import { GetMonitoredProofreadersParams }              from '@models/proofreader/get-monitored-proofreaders-params';
import { GetProofreaderReportParams }                  from '@models/proofreader/get-proofreader-report-params';
import { RestPaged }                                   from '@models/rest/rest-paged';
import { RpaUploadReceipt }                            from '@models/proofreader/rpa-upload-receipt';
import { BlockedReport }                               from '@models/proofreader/blocked-report';
import { Proofreader }                                 from '@models/proofreader/proofreader';
import { Monitoring }                                  from '@models/proofreader/monitoring';
import { RestPaginationAndSortingParams }              from '@models/rest/rest-pagination-and-sorting-params';
import { ProofreaderMonitoringSummary }                from '@models/proofreader/proofreader-monitoring-summary';
import { monitoringStatus }                            from '@models/proofreader/monitoring-status';
import { SearchProofreadAlert }                        from '@models/proofreader/searchProofreadAlert';
import { ProofreadAlert }                              from '@models/proofreader/proofread-alert';
import { MonitoringMessage }                           from '@models/proofreader/monitoring-message';
import { MonitoringMessageType }                       from '@models/proofreader/monitoring-message-type';
import { BankOption }                                  from '@models/user/bank-option';
import { PackageMonitoringTable }                      from '@models/proofreader/package-monitoring-table';
import { GetPackageMonitoringTableParams }             from '@models/proofreader/get-package-monitoring-table-params';
import { MonitoringTask }                              from '@models/proofreader/monitoringTask';
import { InvoiceList }                                 from '@models/proofreader/invoices-params';
import { ProofreaderStatusInvoice }                    from '@models/proofreader/proofreader-status-invoice';
import { ProofreaderInvoice }                          from '@models/proofreader/proofreader-invoice';
import { Invoice }                                     from '@models/proofreader/invoice';
import { PostProofreaderInvoiceParams }                from '@models/proofreader/post-proofreader-invoice-params';
import { ProofreaderApplicant }                        from '@models/proofreader/proofreader-applicant';
import { QuestionsParams }                             from '@shared/models/proofreader/questions-params';
import { RecruitmentTestQuestion }                     from '@models/proofreader/recruitment-test-question';
import { GetCandidateParam }                           from '@shared/models/proofreader/get-candidate-param';
import { RecruitmentTest }                             from '@models/proofreader/recruitment-test';
import { Question }                                    from '@models/proofreader/question';
import { QuestionnaireAnswers }                        from '@models/proofreader/questionnaire-answers';
import { Competence }                                  from '@shared/models/proofreader/competence';
import { ProofreaderTraining }                         from '@shared/models/proofreader/proofreader-training';
import { NotifyRecruitmentTestResultsResponse }        from '@models/proofreader/notify-recruitment-test-results-response';
import { GenerateProofreadersRecruitmentTestResponse } from '@models/proofreader/generate-proofreaders-recruitment-test-response';
import { MonitoringProofread }                         from '@shared/models/proofreader/monitoring-proofread';
import { ProofreaderStatusPayment }                    from '@models/proofreader/proofreader-status-payment';
import { ProofreadTestGenre }                          from '@shared/models/proofreading/proofread-test-genre';
import { ErrorService }                                from '@utils/error/error.service';
import { HistoryAlertMonitoring } from '@shared/models/proofreader/history-alert-monitoring';


@Injectable({ providedIn: 'root' })
export class ApiProofreadersService {

  private readonly baseUrl: string;

  constructor(
    private http:         HttpClient,
    private errorService: ErrorService
  ) {
    this.baseUrl = `${ environment.apiUrl }/professores`;
  }

  public getProofreaders(
    term?:   string,
    size?:   number,
    active?: boolean,
    perfilCorretorId?: string,
    categoriaId?: number

  ): Observable<Proofreader[]> {
    const httpParams: HttpParams = TransformClass.httpParams({
      size, search: term, professorAtivo: active, perfilCorretorId, categoriaId
    });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }`,
      { params: httpParams }
    ).pipe(
      map(response => response.data as Proofreader[])
    );
  }

  public uploadRpaFiles(
    files: File[],
    month: number,
    year:  number
  ): Observable<HttpEvent<RestResponse>> {
    const formData               = new FormData(),
          httpParams: HttpParams = TransformClass.httpParams({ mes: month, ano: year });

    for (const file of files) {
      formData.append('listFileRPA', file);
    }

    return this.http.post<RestResponse>(`${ this.baseUrl }/rpa/upload`, formData, {
      params:         httpParams,
      reportProgress: true,
      observe:        'events'
    });
  }

  public setProofreadersRpa(
    params: ProofreaderRpaUpload[],
    month:  number,
    year:   number
  ): Observable<RpaUploadReceipt> {
    const httpParams: HttpParams = TransformClass.httpParams({ mes: month, ano: year });

    return this.http.post<RestResponse>(`${ this.baseUrl }/rpa`, params, { params: httpParams })
               .pipe(map(response => response.data as RpaUploadReceipt));
  }


  public getProofreaderReportExcel(
    professorId: number,
    params:   GetProofreaderReportParams
  ): Observable<Blob> {
    const endpoint: string       = `${ this.baseUrl }/${ professorId }/relatorio/excel`,
          httpParams: HttpParams = TransformClass.httpParams(params),
          httpOptions: object    = { responseType: 'blob', params: httpParams };

    return this.http.get<Blob>(endpoint, httpOptions);
  }

  public getProofreaderReportCsv(
    professorId: number,
    params:   GetProofreaderReportParams
  ): Observable<Blob> {
    const endpoint: string       = `${ this.baseUrl }/${ professorId }/relatorio/csv`,
          httpParams: HttpParams = TransformClass.httpParams(params),
          httpOptions: object    = { responseType: 'blob', params: httpParams };

    return this.http.get<Blob>(endpoint, httpOptions);
  }

  public getReportProofreaders(
    filterParams: SearchReportProofreaders
  ): Observable<ProofreaderReport[]> {

    const httpParams: HttpParams = TransformClass.httpParams( filterParams );

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/relatorio/correcoes`, { params: httpParams }
    ).pipe(map(response => response.data as ProofreaderReport[]));
  }

  public getProofreadAlerts(
    filterParams: SearchProofreadAlert
  ): Observable<ProofreadAlert[]> {

    const httpParams: HttpParams = TransformClass.httpParams(filterParams);

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/relatorio/correcoes/alertas`, { params: httpParams }
    ).pipe(map((response: RestResponse) => response.data as ProofreadAlert[]));
  }

  public getReportBlocked(
    professorId: number,
    datInicio:   string,
    datFim:      string,
  ): Observable<BlockedReport[]> {

    const httpParams: HttpParams = TransformClass.httpParams({ datInicio, datFim });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/${ professorId }/relatorio/barrados`, { params: httpParams }
    ).pipe(map(response => response.data as BlockedReport[]));
  }

  // Lista as monitorias do professor logado
  public getActiveProofreaderMonitoringList(): Observable<ProofreaderMonitoringSummary[]> {
    return this.http.get<RestResponse>(
      `${ this.baseUrl }/monitorias/professores/logado`
    ).pipe(map(response => response.data as ProofreaderMonitoringSummary[]));
  }

  // Lista as monitorias do professor
  public getProofreaderMonitoringList(
    professorId: number
  ): Observable<ProofreaderMonitoringSummary[]> {

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/monitorias/professores/${ professorId }`
    ).pipe(map(response => response.data as ProofreaderMonitoringSummary[]));
  }

  // Busca os dados da monitoria
  public getMonitoring(id: number): Observable<Monitoring> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/monitorias/${ id }`).pipe(
      map(response => response.data as Monitoring)
    );
  }

  public getMonitoringMessage(
    monitoriaCorrecaoItem: MonitoringMessageType,
    tipoConceitoMonitoria: string
  ): Observable<MonitoringMessage> {
    const httpParams: HttpParams = TransformClass.httpParams({ monitoriaCorrecaoItem, tipoConceitoMonitoria });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/monitorias/correcao/mensagem/padrao`,
      { params: httpParams }
    ).pipe(map((response: RestResponse) => response.data as MonitoringMessage));
  }

  // Cadastra ou atualiza uma monitoria
  public editMonitoring(
    params: EditMonitoringParams
  ): Observable<EditMonitoringParams> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/monitorias`, params).pipe(
      map(response => response.data as EditMonitoringParams)
    );
  }

  // Cadastra ou atualiza a monitoria de uma correção de redação
  public editMonitoringProofreads(
    id:         number,
    proofreads: MonitoringProofread[]
  ): Observable<MonitoringProofread[]> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/monitorias/${id}/correcoes`, proofreads).pipe(
      map(response => response.data as MonitoringProofread[])
    );
  }

  // Exclui a monitoria de um professor
  public deleteProofreaderMonitoring(monitoringId: number): Observable<void> {
    return this.http.delete<void>(`${ this.baseUrl }/monitoria/${ monitoringId }`);
  }

  // Exclui a monitoria de uma correção de redação
  public deleteMonitoringProofread(
    monitoringProofreadId: number
  ): Observable<void> {
    return this.http.delete<void>(`${ this.baseUrl }/monitoria/correcao/${ monitoringProofreadId }`);
  }

  public updateMonitoringStatus(
    monitoringId: number,
    status:       monitoringStatus
  ): Observable<void> {

    const httpParams: HttpParams = TransformClass.httpParams({ status });

    return this.http.put<void>(`${ this.baseUrl }/monitoria/${ monitoringId }/status`, {}, {
      params: httpParams
    });
  }

  public sendMonitoringEmail(
    monitoringId:  number,
    proofreaderId: number
  ): Observable<void> {

    const httpParams: HttpParams = TransformClass.httpParams({
      monitoriaId: monitoringId,
      corretorId:  proofreaderId
    });

    return this.http.post<void>(`${ this.baseUrl }/monitorias/email`, {}, {
      params: httpParams
    });
  }

  public getMonitoredProofreaders(
    filterParams:     GetMonitoredProofreadersParams,
    paginationParams: RestPaginationAndSortingParams
  ): Observable<RestPaged<MonitoringReportItem[]>> {
    const httpParams: HttpParams = TransformClass.httpParams({
      ...filterParams,
      ...TransformClass.formatPaginationParams(paginationParams)
    });

    return this.http.get<RestPaged<MonitoringReportItem[]>>(
      `${ this.baseUrl }/monitorados`, { params: httpParams }
    );
  }

  public getAlertHistory(proofreaderId: number): Observable<HistoryAlertMonitoring[]> {
    return this.http.get<RestResponse>(`${this.baseUrl}/${proofreaderId}/email/analise/pacotes`)
    .pipe(map((response: RestResponse) => response.data as HistoryAlertMonitoring[]));
  }

  // Carrega lista dos monitoramentos de pacotes de correção
  public getPackageMonitoringTable(
    proofreaderId: number,
    filterParams:  Partial<GetPackageMonitoringTableParams> = {},
  ): Observable<RestPaged<PackageMonitoringTable>> {
    const httpParams: HttpParams = TransformClass.httpParams({
      ...filterParams
    });

    return this.http.get<RestPaged<PackageMonitoringTable>>(
      `${ this.baseUrl }/${ proofreaderId }/pacotes`, { params: httpParams }
    );
  }

  public getBanks(
    term?: string,
    size?: number
  ): Observable<BankOption[]> {
    const httpParams: HttpParams = TransformClass.httpParams({ size, search: term });

    return this.http.get<RestResponse>(
      `${this.baseUrl}/lista/instituicoesFinanceiras`, { params: httpParams }
    ).pipe(map((response: RestResponse) => response.data as BankOption[]));
  }

  public getInvoices(
    filterParams:     Partial<InvoiceList> = {},
    paginationParams: RestPaginationAndSortingParams = {}
  ): Observable<RestPaged<ProofreaderStatusInvoice[]>> {
    const httpParams: HttpParams = TransformClass.httpParams({
      ...filterParams,
      ...TransformClass.formatPaginationParams(paginationParams),
    });

    return this.http.get<RestPaged<ProofreaderStatusInvoice[]>>(
      `${this.baseUrl}/nfe`, { params: httpParams }
    );
  }


  public getInvoicesExcel(params: InvoiceList): Observable<Blob> {
    const endPoint: string = `${ this.baseUrl }/nfe/excel`,
          httpParams: HttpParams = TransformClass.httpParams(params),
          httpOptions: object = { responseType: 'blob', params: httpParams};

    return this.http.get<Blob>(endPoint, httpOptions);
  }

  public getProofreaderInvoices(): Observable<ProofreaderInvoice> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/logado/nfe`).pipe(
      map((response: RestResponse) => response.data as ProofreaderInvoice)
    );
  }

  /*public deleteInvoice(invoiceId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/nfe/${invoiceId}`);
  }*/

  public getProofreadInvoiceByLogged(ano: number, mes: number): Observable<Invoice> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/logado/nfe/${ano}/${mes}`).pipe(
      map(response => response.data as Invoice)
    );
  }

  public getProofreadInvoiceById(proofreadId: number, ano: number, mes: number): Observable<Invoice> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/${ proofreadId }/nfe/${ ano }/${ mes }`).pipe(
      map(response => response.data as Invoice)
    );
  }

  public postProofreaderInvoice(
    file:  File,
    params: PostProofreaderInvoiceParams
  ): Observable<HttpEvent<RestResponse>> {
    const formData: FormData     = new FormData(),
          httpParams: HttpParams = TransformClass.httpParams(params);

    // for (const file of files) {
    formData.append('file', file);
    // }

    return this.http.post<RestResponse>(`${ this.baseUrl }/nfe/upload`, formData, {
      params:         httpParams,
      reportProgress: true,
      observe:        'events'
    });
  }

  public updatePaymentStatusToInvoice(
    indPago:       boolean,
    ano:           number,
    mes:           number,
    listProfessor: ProofreaderStatusPayment[]
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/nfe/statusPagamento`, { indPago, ano, mes, listProfessor });
  }

  public problemWithPayment(mes: number, ano: number, mensagem: string ): Observable<void> {
    const httpParams: HttpParams = TransformClass.httpParams({ mes, ano, mensagem });
    return this.http.post<void>(`${ this.baseUrl }/logado/nfe/email/problemaPagamento`, {}, { params: httpParams });
  }

  public sendNFEToOobj(mes: number, ano: number, professorId?: number): Observable<void> {
    const httpParams: HttpParams = TransformClass.httpParams({ professorId, mes, ano });
    return this.http.post<void>(`${ this.baseUrl }/nfe/email/oobj`, {}, { params: httpParams });
  }

  public getActiveProofreaderExcel(): Observable<Blob> {
    const endPoint: string    = `${ this.baseUrl }/relatorio/excel`,
          httpOptions: object = { responseType: 'blob' };

    return this.http.get<Blob>(endPoint, httpOptions);
  }

  public getMonitoredTask(
    professorId: number,
    tarefaId?:   number
  ): Observable<MonitoringTask> {
    const httpParams: HttpParams = TransformClass.httpParams({ tarefaId });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/${ professorId }/monitoria/tarefas`, { params: httpParams }
    ).pipe(map((response: RestResponse) => response.data as MonitoringTask));
  }

  public getLastMonitoredTasks(professorId: number): Observable<{ tarefas: number[] }> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/${ professorId }/monitoria/tarefas/ultimas`).pipe(
      map((response: RestResponse) => response.data as { tarefas: number[] })
    );
  }

  public registerApplicant(params: ProofreaderApplicant): Observable<ProofreaderApplicant> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos`, params).pipe(
      map((response: RestResponse) => response.data as ProofreaderApplicant)
    );
  }

  public searchQuestions(
    params: QuestionsParams
  ): Observable<RecruitmentTestQuestion[]> {
    const httpParams: HttpParams = TransformClass.httpParams(params);

    return this.http.get<RestResponse>(
      ` ${ this.baseUrl }/candidatos/provas/questoes`,
      { params: httpParams }
    ).pipe(map((response: RestResponse) => response.data as RecruitmentTestQuestion[]));
  }

  public editQuestion(question: Question): Observable<Question> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos/provas/questoes`, question).pipe(
      map((response: RestResponse) => response.data as Question)
    );
  }

  public uploadImagesToQuestion(file: File): Observable<HttpEvent<RestResponse>> {
    const formData: FormData = new FormData();

    formData.append('imagem', file);

    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos/provas/questoes/uploadImagem`,
      formData, {
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  public getQuestion(questionId: number): Observable<RecruitmentTestQuestion> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/candidatos/provas/questoes/${ questionId }`)
               .pipe(map((response: RestResponse) => response.data as RecruitmentTestQuestion));
  }

  public getProofreaderCandidateList(
    examParam:        GetCandidateParam,
    paginationParams: RestPaginationAndSortingParams
  ): Observable<RestPaged<ProofreaderApplicant[]>> {

    const examParams: HttpParams = TransformClass.httpParams({
      ...examParam,
      ...TransformClass.formatPaginationParams(paginationParams)
    });

    return this.http.get<RestPaged<ProofreaderApplicant[]>>(
      `${ this.baseUrl }/candidatos/provas`, { params: examParams }
    );
  }

  public getCompetence(
    categoriaId: number,
    indAtivo:    boolean = true
  ): Observable<Competence[]> {
    const params: HttpParams = TransformClass.httpParams({ categoriaId, indAtivo });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/candidatos/provas/competencias`, { params }
    ).pipe(map((response: RestResponse) => response.data as Competence[]));
  }

  public getCategoryTest(): Observable<ProofreadTestGenre[]> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/candidatos/provas/categorias`)
               .pipe(map(response => response.data as ProofreadTestGenre[]));
  }

  public toggleQuestionActive(
    questaoId: number,
    indAtivo:  boolean
  ): Observable<void> {
    return this.http.put<void>(
      `${ this.baseUrl }/candidatos/provas/questoes/${ questaoId }/ativa/${ indAtivo }`, {}
    );
  }

  public sendProofreaderTraining(
    treinamentoId: number,
    candidatos:    number[]
  ): Observable<ProofreaderTraining> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos/treinamento`, { treinamentoId, candidatos })
               .pipe(map(response => response.data as ProofreaderTraining));
  }

  public generateProofreadersRecruitmentTest(
    categoriaId: number,
    candidatos:  number[]
  ): Observable<GenerateProofreadersRecruitmentTestResponse> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos/provas/enviarProva`, { categoriaId, candidatos })
               .pipe(map(response => response.data as GenerateProofreadersRecruitmentTestResponse));
  }

  public notifyRecruitmentTestResults(
    candidatos: number[]
  ): Observable<NotifyRecruitmentTestResultsResponse> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/candidatos/provas/emailResultado`, { candidatos })
               .pipe(map(response => response.data as NotifyRecruitmentTestResultsResponse));
  }

  public getRecruitmentTest(uuid: string): Observable<RecruitmentTest> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/candidatos/provas/${ uuid }`)
               .pipe(map((response) => response.data as RecruitmentTest));
  }

  public getRecruitmentTestResults(testId: number): Observable<RecruitmentTest> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/candidatos/provas/resultado/${ testId }`)
               .pipe(map((response) => response.data as RecruitmentTest));
  }

  public saveRecruitmentTest(answers: QuestionnaireAnswers): Observable<void> {
    return this.http.post<void>(`${ this.baseUrl }/candidatos/provas/salvar`, answers);
  }

  public concludeRecruitmentTest(answers: QuestionnaireAnswers): Observable<void> {
    return this.http.post<void>(`${ this.baseUrl }/candidatos/provas/finalizar`, answers);
  }

  public submitRecruitmentTest(
    answers:        QuestionnaireAnswers,
    shouldConclude: boolean,
    errorCallback?: VoidFunction
  ): Observable<void> {

    const endpoint$: Observable<void> = shouldConclude
                                        ? this.concludeRecruitmentTest(answers)
                                        : this.saveRecruitmentTest(answers);

    return endpoint$.pipe(catchError((error) => {
      return this.errorService.handleFormError(error, errorCallback);
    }));
  }

  public deleteQuestion(questionId: number): Observable<void> {
    return this.http.delete<void>(`${ this.baseUrl }/candidatos/provas/questoes/${ questionId }`);
  }

  public getApplicantsListExcel(params: GetCandidateParam): Observable<Blob> {
    const endpoint: string       = `${ this.baseUrl }/candidatos/provas/excel`,
          httpParams: HttpParams = TransformClass.httpParams(params),
          httpOptions: object    = { responseType: 'blob', params: httpParams };

    return this.http.get<Blob>(endpoint, httpOptions);
  }

  public setTrainingModeManually(proofreaderId: number, indTreinamento: boolean): Observable<void> {
    return this.http.put<void>(`${ this.baseUrl }/candidatos/${ proofreaderId }/treinamento/${ indTreinamento }`, {} );
  }

}
